import API from "./api";

const AssignmentService = {
  // ============ ADMIN FUNCTIONS ============

  // Get all assignments for a batch (with pagination and search)
  getBatchAssignments: async (batchId, page = 1, perPage = 10, search = "") => {
    try {
      const response = await API.get(`/assignments/batch/${batchId}`, {
        params: { page, per_page: perPage, search },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching batch assignments:", error);
      throw error;
    }
  },

  // Get single assignment by ID
  getAssignment: async (assignmentId) => {
    try {
      const response = await API.get(`/assignments/${assignmentId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching assignment:", error);
      throw error;
    }
  },

  // Create new assignment (multipart/form-data for file upload)
  createAssignment: async (formData) => {
    try {
      const response = await API.post("/assignments", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      console.error("Error creating assignment:", error);
      throw error;
    }
  },

  // Update existing assignment
  updateAssignment: async (assignmentId, formData) => {
    try {
      // The backend expects POST with _method=PUT for FormData
      // Make sure _method is in the formData
      console.log("Updating assignment ID:", assignmentId);
      console.log("FormData contents:");
      for (let pair of formData.entries()) {
        console.log(pair[0] + ": " + pair[1]);
      }

      const response = await API.post(
        `/assignments/${assignmentId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      console.log("Update response:", response);
      return response.data;
    } catch (error) {
      console.error("Error updating assignment:", error);
      console.error("Error response:", error.response);
      throw error;
    }
  },

  // Delete assignment
  deleteAssignment: async (assignmentId) => {
    try {
      const response = await API.delete(`/assignments/${assignmentId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting assignment:", error);
      throw error;
    }
  },

  // Update assignment status (publish/close)
  updateAssignmentStatus: async (assignmentId, status) => {
    try {
      const response = await API.post(`/assignments/${assignmentId}/status`, {
        status,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating assignment status:", error);
      throw error;
    }
  },

  // Get all submissions for an assignment (for grading)
  getSubmissions: async (assignmentId, page = 1, perPage = 10) => {
    try {
      const response = await API.get(
        `/assignments/${assignmentId}/submissions`,
        {
          params: { page, per_page: perPage },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching submissions:", error);
      throw error;
    }
  },

  // Grade a submission
  gradeSubmission: async (submissionId, obtainedMarks, feedback) => {
    try {
      const response = await API.put(
        `/assignments/submissions/${submissionId}/grade`,
        {
          obtained_marks: obtainedMarks,
          feedback: feedback,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error grading submission:", error);
      throw error;
    }
  },

  // Download assignment file
  downloadAssignmentFile: async (assignmentId) => {
    try {
      const response = await API.get(`/assignments/${assignmentId}/download`, {
        responseType: "blob",
      });

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers["content-disposition"];
      let filename = "assignment.pdf";
      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, "");
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error("Error downloading file:", error);
      throw error;
    }
  },

  // ============ STUDENT FUNCTIONS ============

  // Get assignments for logged-in student
  getMyAssignments: async () => {
    try {
      const response = await API.get("/assignments/my");
      return response.data;
    } catch (error) {
      console.error("Error fetching my assignments:", error);
      throw error;
    }
  },

  // Get single assignment with student's submission
  getAssignmentForStudent: async (assignmentId) => {
    try {
      const response = await API.get(`/assignments/${assignmentId}/student`);
      return response.data;
    } catch (error) {
      console.error("Error fetching assignment for student:", error);
      throw error;
    }
  },

  // Get single assignment with student's submission (FIXED - This was missing)
  getStudentAssignment: async (assignmentId) => {
    try {
      const response = await API.get(`/assignments/${assignmentId}/student`);
      return response.data;
    } catch (error) {
      console.error("Error fetching assignment for student:", error);
      throw error;
    }
  },

  // Submit assignment (student)
  submitAssignment: async (assignmentId, formData) => {
    try {
      const response = await API.post(
        `/assignments/${assignmentId}/submit`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error submitting assignment:", error);
      throw error;
    }
  },

  downloadSubmissionFile: async (submissionId) => {
    try {
      const response = await API.get(
        `/assignments/submissions/${submissionId}/download`,
        {
          responseType: "blob",
        },
      );

      const contentDisposition = response.headers["content-disposition"];
      let filename = "submission.pdf";
      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, "");
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error("Error downloading submission:", error);
      throw error;
    }
  },
};

export default AssignmentService;
